import React, { useState, useEffect } from 'react';
import { 
  Video, 
  Mic, 
  Users, 
  Calendar, 
  Clock, 
  Play, 
  Pause, 
  Square, 
  Settings, 
  MessageSquare,
  Share2,
  Download,
  Eye,
  Heart,
  Star,
  AlertCircle,
  CheckCircle,
  XCircle,
  Plus,
  Edit,
  Trash2
} from 'lucide-react';

interface LiveSession {
  id: string;
  title: string;
  description: string;
  host: string;
  status: 'scheduled' | 'live' | 'ended' | 'cancelled';
  startTime: Date;
  endTime: Date;
  duration: number; // in minutes
  maxParticipants: number;
  currentParticipants: number;
  category: 'consultation' | 'workshop' | 'group-therapy' | 'education' | 'q&a';
  tags: string[];
  thumbnail?: string;
  recordingUrl?: string;
  chatEnabled: boolean;
  recordingEnabled: boolean;
  isPrivate: boolean;
  price?: number;
  currency?: string;
}

interface LiveSessionsProps {
  onBack?: () => void;
}

export const LiveSessions: React.FC<LiveSessionsProps> = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState<'upcoming' | 'live' | 'past' | 'create'>('upcoming');
  const [sessions, setSessions] = useState<LiveSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<LiveSession | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  // Mock sessions data
  useEffect(() => {
    const mockSessions: LiveSession[] = [
      {
        id: '1',
        title: 'Diabetes Management Workshop',
        description: 'Comprehensive workshop on managing diabetes through diet, exercise, and medication.',
        host: 'Dr. Sarah Johnson',
        status: 'scheduled',
        startTime: new Date('2025-01-20T14:00:00'),
        endTime: new Date('2025-01-20T15:30:00'),
        duration: 90,
        maxParticipants: 50,
        currentParticipants: 23,
        category: 'workshop',
        tags: ['diabetes', 'nutrition', 'health'],
        chatEnabled: true,
        recordingEnabled: true,
        isPrivate: false,
        price: 25,
        currency: 'USD'
      },
      {
        id: '2',
        title: 'Stress Relief Meditation Session',
        description: 'Guided meditation session for stress relief and mental wellness.',
        host: 'Dr. Emily Rodriguez',
        status: 'live',
        startTime: new Date('2025-01-15T10:00:00'),
        endTime: new Date('2025-01-15T11:00:00'),
        duration: 60,
        maxParticipants: 100,
        currentParticipants: 67,
        category: 'group-therapy',
        tags: ['meditation', 'mental-health', 'stress'],
        chatEnabled: true,
        recordingEnabled: true,
        isPrivate: false,
        price: 15,
        currency: 'USD'
      },
      {
        id: '3',
        title: 'Heart Health Q&A Session',
        description: 'Interactive Q&A session with cardiologist Dr. Michael Chen.',
        host: 'Dr. Michael Chen',
        status: 'ended',
        startTime: new Date('2025-01-10T16:00:00'),
        endTime: new Date('2025-01-10T17:00:00'),
        duration: 60,
        maxParticipants: 75,
        currentParticipants: 45,
        category: 'q&a',
        tags: ['cardiology', 'heart-health', 'q&a'],
        chatEnabled: true,
        recordingEnabled: true,
        isPrivate: false,
        recordingUrl: '/recordings/heart-health-qa-2025-01-10.mp4'
      }
    ];
    setSessions(mockSessions);
  }, []);

  const filteredSessions = sessions.filter(session => {
    const matchesSearch = session.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         session.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         session.host.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || session.category === categoryFilter;
    const matchesTab = 
      (activeTab === 'upcoming' && session.status === 'scheduled') ||
      (activeTab === 'live' && session.status === 'live') ||
      (activeTab === 'past' && session.status === 'ended');
    
    return matchesSearch && matchesCategory && matchesTab;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'live': return 'bg-red-100 text-red-800';
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'ended': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'consultation': return 'bg-purple-100 text-purple-800';
      case 'workshop': return 'bg-green-100 text-green-800';
      case 'group-therapy': return 'bg-pink-100 text-pink-800';
      case 'education': return 'bg-blue-100 text-blue-800';
      case 'q&a': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const sessionStats = {
    total: sessions.length,
    upcoming: sessions.filter(s => s.status === 'scheduled').length,
    live: sessions.filter(s => s.status === 'live').length,
    past: sessions.filter(s => s.status === 'ended').length,
    totalParticipants: sessions.reduce((sum, s) => sum + s.currentParticipants, 0),
    totalRevenue: sessions.reduce((sum, s) => sum + (s.price || 0) * s.currentParticipants, 0)
  };

  const formatTime = (date: Date) => {
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Live Sessions</h1>
          <p className="text-slate-600">Host and join live video sessions and consultations</p>
        </div>
        <div className="flex items-center space-x-3">
          {onBack && (
            <button
              onClick={onBack}
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-lg transition-colors"
            >
              ← Back
            </button>
          )}
          <button
            onClick={() => setActiveTab('create')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Create Session</span>
          </button>
        </div>
      </div>

      {/* Session Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Total Sessions</p>
              <p className="text-2xl font-bold text-slate-800">{sessionStats.total}</p>
            </div>
            <Video className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Upcoming</p>
              <p className="text-2xl font-bold text-slate-800">{sessionStats.upcoming}</p>
            </div>
            <Calendar className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Live Now</p>
              <p className="text-2xl font-bold text-slate-800">{sessionStats.live}</p>
            </div>
            <Play className="h-8 w-8 text-red-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Past Sessions</p>
              <p className="text-2xl font-bold text-slate-800">{sessionStats.past}</p>
            </div>
            <Clock className="h-8 w-8 text-gray-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Total Participants</p>
              <p className="text-2xl font-bold text-slate-800">{sessionStats.totalParticipants}</p>
            </div>
            <Users className="h-8 w-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Total Revenue</p>
              <p className="text-2xl font-bold text-slate-800">${sessionStats.totalRevenue}</p>
            </div>
            <Star className="h-8 w-8 text-yellow-500" />
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="border-b border-slate-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('upcoming')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'upcoming'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              Upcoming
            </button>
            <button
              onClick={() => setActiveTab('live')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'live'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              Live Now
            </button>
            <button
              onClick={() => setActiveTab('past')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'past'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              Past Sessions
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Search and Filter */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search sessions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              <option value="consultation">Consultation</option>
              <option value="workshop">Workshop</option>
              <option value="group-therapy">Group Therapy</option>
              <option value="education">Education</option>
              <option value="q&a">Q&A</option>
            </select>
          </div>

          {/* Sessions List */}
          <div className="space-y-4">
            {filteredSessions.map((session) => (
              <div key={session.id} className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-semibold text-slate-800 mb-2">{session.title}</h3>
                        <p className="text-slate-600 mb-3">{session.description}</p>
                        
                        <div className="flex items-center space-x-4 mb-3">
                          <span className="text-sm text-slate-500">Host: {session.host}</span>
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(session.status)}`}>
                            {session.status}
                          </span>
                          <span className={`px-2 py-1 text-xs rounded-full ${getCategoryColor(session.category)}`}>
                            {session.category.replace('-', ' ')}
                          </span>
                        </div>

                        <div className="flex items-center space-x-6 text-sm text-slate-500">
                          <span className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            {formatTime(session.startTime)}
                          </span>
                          <span className="flex items-center">
                            <Clock className="h-4 w-4 mr-1" />
                            {session.duration} min
                          </span>
                          <span className="flex items-center">
                            <Users className="h-4 w-4 mr-1" />
                            {session.currentParticipants}/{session.maxParticipants} participants
                          </span>
                          {session.price && (
                            <span className="font-semibold text-green-600">
                              ${session.price} {session.currency}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Session Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {session.tags.map((tag, index) => (
                        <span key={index} className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Session Features */}
                    <div className="flex items-center space-x-4 text-sm text-slate-500">
                      {session.chatEnabled && (
                        <span className="flex items-center">
                          <MessageSquare className="h-4 w-4 mr-1" />
                          Chat enabled
                        </span>
                      )}
                      {session.recordingEnabled && (
                        <span className="flex items-center">
                          <Video className="h-4 w-4 mr-1" />
                          Recording enabled
                        </span>
                      )}
                      {session.isPrivate && (
                        <span className="flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          Private session
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    {session.status === 'live' && (
                      <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
                        <Play className="h-4 w-4" />
                        <span>Join Live</span>
                      </button>
                    )}
                    {session.status === 'scheduled' && (
                      <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
                        <Calendar className="h-4 w-4" />
                        <span>Join</span>
                      </button>
                    )}
                    {session.status === 'ended' && session.recordingUrl && (
                      <button className="bg-slate-600 hover:bg-slate-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
                        <Download className="h-4 w-4" />
                        <span>Watch Recording</span>
                      </button>
                    )}
                    
                    <div className="flex flex-col space-y-1">
                      <button className="p-2 text-slate-400 hover:text-slate-600 transition-colors">
                        <Edit className="h-4 w-4" />
                      </button>
                      <button className="p-2 text-slate-400 hover:text-slate-600 transition-colors">
                        <Share2 className="h-4 w-4" />
                      </button>
                      <button className="p-2 text-slate-400 hover:text-red-600 transition-colors">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {filteredSessions.length === 0 && (
              <div className="text-center py-12">
                <Video className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-800 mb-2">No sessions found</h3>
                <p className="text-slate-600">No sessions match your current filters.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Session Form */}
      {activeTab === 'create' && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-6">Create New Session</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Session Title</label>
                <input
                  type="text"
                  placeholder="Enter session title..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                <textarea
                  rows={3}
                  placeholder="Enter session description..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Category</label>
                <select className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="consultation">Consultation</option>
                  <option value="workshop">Workshop</option>
                  <option value="group-therapy">Group Therapy</option>
                  <option value="education">Education</option>
                  <option value="q&a">Q&A</option>
                </select>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Start Date & Time</label>
                <input
                  type="datetime-local"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Duration (minutes)</label>
                <input
                  type="number"
                  min="15"
                  max="480"
                  placeholder="60"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Max Participants</label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  placeholder="50"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Price (optional)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <div className="mt-6 space-y-4">
            <h4 className="font-medium text-slate-800">Session Settings</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="flex items-center space-x-2">
                <input type="checkbox" defaultChecked className="rounded" />
                <span className="text-sm text-slate-700">Enable chat</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" defaultChecked className="rounded" />
                <span className="text-sm text-slate-700">Record session</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="rounded" />
                <span className="text-sm text-slate-700">Private session</span>
              </label>
            </div>
          </div>

          <div className="flex space-x-4 mt-6">
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors">
              Create Session
            </button>
            <button 
              onClick={() => setActiveTab('upcoming')}
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-6 py-2 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}; 